<template>
  <div class="i-personality-section">
    <div
      v-if="data.chartDataAvailable"
      class="main"
    >
      <div class="header">
        <div class="sub-header">Personality Type</div>
        <div><strong>Less than 5%</strong> of people share <br />your top traits and skills.</div>
      </div>
      <div class="radar">
        <iRadarChart
          class="chart"
          v-bind="data.capboiChart"
        />
        <div class="key">
          <iBasePillTag
            is-bold
            class="ideal-career-tag"
            :circle-color="amethyst500"
            tag-color="transparent"
            :style="{ borderColor: amethyst500 }"
            :tag-text="userStore.getFullName || 'You'"
            :text-style="amethyst500"
          />
          <iBasePillTag
            is-bold
            class="ideal-career-tag"
            :circle-color="chartColorOther"
            tag-color="transparent"
            :style="{ borderColor: chartColorOther }"
            :tag-text="`Peers, ${reportStore.getPeerGroupTitle || 'similar age'}`"
            :text-style="chartColorOther"
          />
        </div>
      </div>
    </div>
    <div class="analysis">
      <div class="header">
        <div>
          You're {{ getArticle(personalityType) }} <strong>{{ personalityType }}.</strong>
        </div>
      </div>
      <div
        v-if="!!personalityDescriptors.length"
        class="row-list"
      >
        <div
          v-for="descriptor in personalityDescriptors"
          :key="descriptor"
          class="badge"
        >
          <div class="inner">{{ descriptor }}</div>
        </div>
      </div>
      <div class="description">
        {{ personalityDescription }}
      </div>
      <div
        v-if="!!strengthItems"
        class="strength-items"
      >
        <div>Core competencies & Strengths</div>
        <div class="set">
          <div
            v-for="strength in Object.keys(strengthItems)"
            :key="strength"
            class="strength"
          >
            {{ strength }}
          </div>
        </div>
      </div>
      <div
        v-if="!!strengthSummary"
        class="strength-summary"
      >
        <div class="title">{{ userStore.getFullName?.split(" ")[0] }}'s Strengths Summary</div>
        {{ strengthSummary }}
      </div>
    </div>
    <div class="weakness">
      <div class="header">
        <div>Weaknesses & <br />Areas of Improvement</div>
      </div>
      <div class="action">
        <iBaseButton
          class="button"
          v-bind="buttonStyle"
          text="Unlock My Improvement Areas"
          @clicked="$emit('scroll-to-payment')"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive } from "vue"
import { useReportStore } from "@/stores/report"
import { useUserStore } from "@/stores/user"
import { amethyst500, black200, black800, bluePurpleGradient, chartColorOther, white } from "@/styles/colors"
import { breakpointSm } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"
import { getArticle } from "@/composables/getArticle"

import iRadarChart from "@/components/global/RadarChart.vue"
import iBaseButton from "@/components/base/BaseButton.vue"
import iBasePillTag from "@/components/base/BasePillTag.vue"

// TYPES
interface ChartModel {
  canvasId: string
  chartId: string
  colorMe: string
  colorOther: string
  gridColor: string
  labels: (string | string[])[]
  labelColor: string
  labelHighlightColor: string
  maxDiameter: string
  range: { minimum: number; maximum: number }
  scoresMe: number[]
  scoresOther: number[]
  steps: number
}

// EMITS
defineEmits(["scroll-to-payment"])

// STORES
const reportStore = useReportStore()
const userStore = useUserStore()

// COMPOSABLES
const isSmOrLarger = useIsScreenSize(breakpointSm)

// DATA
const data = reactive({
  chartDataAvailable: false,
  capboiChart: {} as ChartModel
})

//COMPUTED
const buttonStyle = computed(() => {
  return {
    backgroundImage: bluePurpleGradient,
    borderRadius: "8px",
    fontWeight: "500",
    textColor: white,
    width: "max-content",
    padding: isSmOrLarger.value ? "8px 70px" : "10px 18px",
    size: "medium"
  }
})
const strengthSummary = computed(() => reportStore.getStrengthSummary)
const strengthItems = computed(() => reportStore.getStrengthItems)
const personalityType = computed(() => reportStore.reportData?.results?.personalityType)
const personalityDescription = computed(() => reportStore.reportData?.results?.personalityDescription)
const personalityDescriptors = computed(() => reportStore.reportData?.results?.personalityDescriptors)

// FUNCTIONS
function setChartsData() {
  data.capboiChart = {
    colorMe: "#d5adffc7",
    colorOther: chartColorOther,
    maxDiameter: "350px",
    steps: 4,
    gridColor: black200,
    labelColor: black800,
    labelHighlightColor: amethyst500,
    canvasId: "capboiCanvas1",
    chartId: "capboiChart1",
    labels: reportStore.getCapboiLabels,
    range: reportStore.getCapboiRange,
    scoresMe: reportStore.getCapboiScores,
    scoresOther: reportStore.getCapboiScoresPeer
  } as ChartModel

  data.chartDataAvailable = true
}

// LIFECYCLE HOOKS
onMounted(async () => {
  setChartsData()
})
</script>

<style scoped lang="scss">
.i-personality-section {
  @include flex(column, center, center, $gap: 50px);

  padding: 50px 15px;
  background-image: url("@/assets/confirm/bg-gradient-multi.png");
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;

  @media screen and (min-width: $breakpoint-md) {
    gap: 60px;
    padding: 60px 15px;
  }

  & > div {
    @include flex(column, center, center, $gap: 25px);
  }

  .header {
    @include display-md;
    @include flex(column, start, center, $gap: 20px);

    text-align: center;

    @media screen and (min-width: $breakpoint-md) {
      @include display-lg;
    }

    .sub-header {
      @include text-xl;

      font-weight: 700;
      text-align: center;
    }
  }

  .main {
    width: 100%;

    .radar {
      @include flex(column, space-between, end);
      @include border-radius-md;

      background-color: $white;
      padding: 24px;
      width: 100%;
      max-width: 750px;
      min-height: 300px;

      @media screen and (min-width: $breakpoint-xxs) {
        min-height: 400px;
      }

      @media screen and (min-width: $breakpoint-xs) {
        min-height: 440px;
      }

      @media screen and (min-width: $breakpoint-sm) {
        min-height: 423px;
      }

      @media screen and (min-width: $breakpoint-lg) {
        width: 750px;
      }

      .chart {
        display: block;
      }

      .key {
        @include flex(column, end, end, $gap: 5px);

        @media screen and (min-width: $breakpoint-sm) {
          @include flex(row, end, center, $gap: 10px);
        }

        > div {
          @include border-radius-sm;

          border: 2px solid;
        }
      }
    }
  }

  .analysis {
    @include text-sm;

    .row-list {
      @include flex(row, center, center, wrap, $gap: 6px 8px);
      @include text-sm;

      font-weight: 400;

      @media screen and (min-width: $breakpoint-sm) {
        gap: 6px 20px;
      }

      .badge {
        @include border-radius-xxl;

        font-weight: 600;
        width: fit-content;
        background-image: $blue-purple-gradient;
        padding: 1px;
        text-transform: uppercase;

        .inner {
          @include border-radius-xxl;

          padding: 4px 12px;
          background-color: $white;
          mix-blend-mode: screen;
        }
      }
    }

    .description {
      text-align: center;
      margin: 15px 0;
      width: 100%;
      max-width: 550px;

      @media screen and (min-width: $breakpoint-lg) {
        width: 550px;
      }
    }

    .strength-items {
      @include text-xl;

      text-align: center;
      font-weight: 700;
      margin-bottom: 40px;
      width: 100%;

      .set {
        @include border-radius-md;
        @include grid($cols: 1fr, $rows: 1fr 1fr 1fr, $align-items: center);

        background-color: #ffffff4c;
        width: 100%;
        max-width: 750px;
        padding: 0;
        margin-top: 25px;

        @media screen and (min-width: $breakpoint-md) {
          @include grid($cols: 1fr 1fr 1fr, $rows: 1fr, $align-items: center);

          padding: 15px 0;
        }

        @media screen and (min-width: $breakpoint-lg) {
          width: 750px;
        }

        .strength {
          padding: 15px 5px;
          text-align: center;

          @media screen and (min-width: $breakpoint-md) {
            padding: 5px;
          }

          &:not(:last-child) {
            border-bottom: 1px solid $comet200;

            @media screen and (min-width: $breakpoint-md) {
              border: unset;
              border-right: 1px solid $comet200;
            }
          }
        }
      }
    }

    .strength-summary {
      @include flex(column, start, stretch, $gap: 15px);
      @include border-radius-md;

      background-color: $white;
      padding: 24px;
      width: 100%;
      max-width: 750px;

      @media screen and (min-width: $breakpoint-lg) {
        width: 750px;
      }

      .title {
        @include text-xl;

        font-weight: 700;
      }
    }
  }

  .weakness {
    width: 100%;

    .action {
      @include flex(column, center, center);

      background-image: url("@/assets/welcome/weakness-blurred-mob.avif");
      background-size: cover;
      background-repeat: no-repeat;
      background-position: center top;
      width: 100%;
      height: 243px;

      @media screen and (min-width: $breakpoint-md) {
        background-image: url("@/assets/welcome/weakness-blurred.avif");
        background-position: center center;
        width: 750px;
        height: 350px;
      }

      @media screen and (min-width: $breakpoint-lg) {
        width: 900px;
        height: 400px;
      }
    }
  }
}
</style>
